import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";
import {
  cityFaq,
  cityFaqTitle,
  cityMetaTitles,
  cityServicesTitle,
  serviceLinks,
} from "@/i18n/seoPages";

const SITE = "https://reflectumpellicule.com";

const CITY_IMAGES = {
  montreal: [
    { src: "/images/miroir-4.webp", w: 923, h: 2000 },
    { src: "/images/miroir-1.webp", w: 923, h: 2000 },
    { src: "/images/vue-exterieure.webp", w: 923, h: 2000 },
  ],
  laval: [
    { src: "/images/residentiel.webp", w: 1600, h: 2400 },
    { src: "/images/miroir-2.webp", w: 923, h: 2000 },
    { src: "/images/miroir-3.webp", w: 2000, h: 1500 },
  ],
  longueuil: [
    { src: "/images/miroir-3.webp", w: 2000, h: 1500 },
    { src: "/images/vue-interieure.webp", w: 923, h: 2000 },
    { src: "/images/miroir-4.webp", w: 923, h: 2000 },
  ],
  brossard: [
    { src: "/images/miroir-2.webp", w: 923, h: 2000 },
    { src: "/images/miroir-1.webp", w: 923, h: 2000 },
    { src: "/images/vue-exterieure.webp", w: 923, h: 2000 },
  ],
  "rive-nord": [
    { src: "/images/residentiel.webp", w: 1600, h: 2400 },
    { src: "/images/miroir-3.webp", w: 2000, h: 1500 },
    { src: "/images/vue-interieure.webp", w: 923, h: 2000 },
  ],
  "rive-sud": [
    { src: "/images/miroir-1.webp", w: 923, h: 2000 },
    { src: "/images/miroir-2.webp", w: 923, h: 2000 },
    { src: "/images/miroir-3.webp", w: 2000, h: 1500 },
  ],
};

const CITY_PATHS = {
  montreal: "/pellicule-fenetre-montreal",
  laval: "/pellicule-fenetre-laval",
  longueuil: "/pellicule-fenetre-longueuil",
  brossard: "/pellicule-fenetre-brossard", "rive-nord": "/secteurs/rive-nord", "rive-sud": "/secteurs/rive-sud",
};

const VALID = ["montreal", "laval", "longueuil", "brossard", "rive-nord", "rive-sud"];

export default function CityPage({ fixedCity }) {
  const { city: paramCity } = useParams();
  const city = fixedCity || paramCity;
  const { t, lang } = useLanguage();

  const c = VALID.includes(city) ? t(`cities.items.${city}`) : null;
  const faqs = VALID.includes(city) ? cityFaq[lang][city] : null;

  useEffect(() => {
    if (!c) return undefined;
    document.title = cityMetaTitles[lang][city];
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", c.metaDesc);
    const canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", `${SITE}${CITY_PATHS[city]}`);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOg = ogUrl?.getAttribute("content");
    if (ogUrl) ogUrl.setAttribute("content", `${SITE}${CITY_PATHS[city]}`);
    return () => {
      if (canonical && prev) canonical.setAttribute("href", prev);
      if (ogUrl && prevOg) ogUrl.setAttribute("content", prevOg);
    };
  }, [c, lang, city]);

  if (!c) return <Navigate to="/" replace />;

  const images = CITY_IMAGES[city];
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="animate-fade-up">
      <JsonLd id={`faq-city-${city}`} data={faqLd} />

      {/* HERO */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">
              {t("cities.eyebrow")}, {c.name}
            </div>
            <h1 data-testid="city-title" className="font-heading text-[2.5rem] sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4">
              {c.title}
            </h1>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">{c.intro}</p>
            <Link to="/contact" data-testid="city-cta" className="block sm:inline-block">
              <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
                {t("cities.cta")}
                <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
              </Button>
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.06)]">
            <img
              src={images[0].src}
              alt={c.title}
              width={images[0].w}
              height={images[0].h}
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* POINTS */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <ul className="space-y-6">
            {c.points.map((p, i) => (
              <li key={p} data-testid={`city-point-${i}`} className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50 border border-slate-200">
                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 grid place-items-center flex-shrink-0">
                  <Check className="w-4 h-4" strokeWidth={2.6} />
                </span>
                <span className="text-slate-800 leading-relaxed text-lg">{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 grid grid-cols-2 gap-4">
            {images.slice(1).map((img, i) => (
              <figure key={img.src} data-testid={`city-photo-${i}`} className="aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100">
                <img
                  src={img.src}
                  alt={`${t("cities.eyebrow")}, ${c.name}`}
                  width={img.w}
                  height={img.h}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ LOCALE */}
      <section data-testid="city-faq" className="py-20 sm:py-28 lg:py-40 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl lg:text-4xl font-normal tracking-tight leading-[1.25] text-slate-900 mb-12">
            {cityFaqTitle[lang]}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger
                  data-testid={`city-faq-trigger-${i}`}
                  className="text-left font-heading text-lg font-medium text-slate-900 hover:text-blue-600"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`city-faq-content-${i}`} className="text-slate-600 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* LIENS SERVICES */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600 mb-6">
            {cityServicesTitle[lang]}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceLinks[lang].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`city-service-${l.to.slice(1)}`}
                className="px-4 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm text-slate-700 hover:border-blue-300 hover:text-blue-700 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
